import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, EditableCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, toParagraph } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { Lightbox, useLightbox } from '../utils/lightbox';
import { CAT_LABEL, catFromHash, galleryStore, photoTiles, placeTiles, type GalleryTile } from '../utils/gallery';
import type { Poi } from '../utils/places';
import { PATHS, scrollToId } from '../utils/site';
import { useStore } from '../utils/store';

interface Photo {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

const mosaic = 'grid grid-flow-row-dense grid-cols-4 gap-6 max-[767px]:grid-cols-2 max-[767px]:gap-3';

const Expand = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" />
  </svg>
);

/**
 * Every photo in a mosaic: each run of five is one large square and four small ones, the large square
 * alternating sides row by row. Neighborhood photos (from the places nearby) follow in their own block.
 */
export function GalleryMosaic() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const lb = useLightbox();
  const photos = useCollectionBinding<Photo>('gallerymosaic.photos', { collection: 'photo_gallery' });
  const places = useCollectionBinding<Poi>('gallerymosaic.places', { collection: 'neighborhood_pois', defaultLimit: 12 });
  const { cat } = useStore(galleryStore);

  useEffect(() => {
    const apply = (scroll: boolean) => {
      const c = catFromHash(location.hash);
      if (!c) return;
      galleryStore.set({ cat: c });
      if (scroll) scrollToId('gallery-photos');
    };
    apply(false);
    const onHash = () => apply(true);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const own = photoTiles(photos).filter((t) => cat === 'all' || t.cat === cat);
  const near = cat === 'all' || cat === 'neighborhood' ? placeTiles(places) : [];
  const total = own.length + near.length;
  const lbItems = [...own, ...near].map((t) => ({ src: t.src, alt: t.alt, caption: t.caption, chip: CAT_LABEL[t.cat], desc: t.desc }));
  const city = clean(p.property_address_city);

  if (!total && !editing) return null;

  /** Only full runs of five get the large square; a short trailing run would leave holes beside it. */
  const tile = (t: GalleryTile, i: number, offset: number, count: number) => (
    <button
      key={`${t.src}-${i}`}
      className={`group/tile relative block aspect-square cursor-zoom-in overflow-hidden rounded-lg bg-[--q-surface] [&_img]:absolute [&_img]:inset-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:[transition:transform_var(--q-motion-slow)_var(--q-ease-out)] [&:hover_img]:[transform:scale(1.04)] [&:focus-visible_img]:[transform:scale(1.04)] motion-reduce:[&_img]:[transition:none] motion-reduce:[&:hover_img]:[transform:none] max-[767px]:rounded-md${
        i < count - (count % 5)
          ? ' [&:nth-child(10n+1)]:[grid-area:span_2_/_span_2] [&:nth-child(10n+6)]:[grid-area:span_2_/_3_/_auto_/_span_2] max-[767px]:[&:nth-child(5n+1)]:aspect-[358/300] max-[767px]:[&:nth-child(5n+1)]:[grid-area:auto_/_1_/_auto_/_-1]'
          : ''
      }`}
      type="button"
      onClick={() => lb.open(offset + i)}
      aria-label={`Open photo: ${t.caption || t.alt || 'photo'}`}
    >
      <VibesitesImage url={t.src} alt={t.alt} sizes="(min-width: 1200px) 50vw, (min-width: 768px) 50vw, 100vw" />
      <span
        className="absolute inset-0 bg-[linear-gradient(180deg,transparent_30%,color-mix(in_srgb,var(--q-ink)_45%,transparent)_60%,color-mix(in_srgb,var(--q-ink)_85%,transparent)_100%)] opacity-0 [transition:opacity_var(--q-motion-normal)_var(--q-ease-out)] group-hover/tile:opacity-100 group-focus-visible/tile:opacity-100 motion-reduce:[transition:none]"
        aria-hidden="true"
      >
        <span className="absolute right-4 top-4 inline-flex h-11 w-11 scale-90 items-center justify-center rounded-full bg-[--q-paper] text-[--q-ink] [transition:transform_var(--q-motion-normal)_var(--q-ease-out)] group-hover/tile:scale-100 group-focus-visible/tile:scale-100 motion-reduce:[transition:none] max-[767px]:right-3 max-[767px]:top-3 max-[767px]:h-9 max-[767px]:w-9 [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-linejoin:round] [&_svg]:[stroke-width:1.6]">
          <Expand />
        </span>
        <span className="absolute bottom-5 left-5 right-5 flex translate-y-2 flex-col items-start gap-2 text-left text-[--q-on-image] [transition:transform_var(--q-motion-slow)_var(--q-ease-out)] group-hover/tile:translate-y-0 group-focus-visible/tile:translate-y-0 motion-reduce:translate-y-0 motion-reduce:[transition:none] max-[767px]:bottom-3 max-[767px]:left-3 max-[767px]:right-3 max-[767px]:gap-1.5">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-medium uppercase leading-[14px] tracking-[--q-tracking-label]">
            <span className="rounded-full border border-solid border-[color-mix(in_srgb,var(--q-on-image)_55%,transparent)] px-2.5 py-1 backdrop-blur-sm">{CAT_LABEL[t.cat]}</span>
            {t.meta && <span className="opacity-85 max-[767px]:hidden">{t.meta}</span>}
          </span>
          {t.desc && (
            <span className="line-clamp-2 max-w-[48ch] text-[13px] leading-[18px] opacity-90 max-[1199px]:line-clamp-1 max-[767px]:hidden">{t.desc}</span>
          )}
          <span className="mt-1 inline-flex items-center gap-1.5 border-0 border-b border-solid border-current pb-0.5 text-[12px] font-medium leading-4 max-[1199px]:hidden">
            View photo
          </span>
        </span>
      </span>
    </button>
  );

  return (
    <section className="flex flex-col gap-6 px-[--gutter] pb-4xl pt-14 max-[1199px]:pt-10 max-[767px]:gap-4 max-[767px]:pb-14 max-[767px]:pt-6">
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm leading-5 text-[--q-muted] max-[767px]:text-[13px] max-[767px]:leading-[18px]" aria-live="polite">
          {total} {total === 1 ? 'photo' : 'photos'}
        </div>
        <div className="eyebrow max-[767px]:hidden">{cat === 'all' ? 'All photos' : CAT_LABEL[cat]}</div>
      </div>

      <EditableCollectionBinding bindingPath="gallerymosaic.photos" label="Gallery Photos" className={mosaic}>
        {own.map((t, i) => tile(t, i, 0, own.length))}
      </EditableCollectionBinding>

      <EditableCollectionBinding bindingPath="gallerymosaic.places" label="Neighborhood Photos" className="flex flex-col gap-6 empty:hidden">
        {near.length > 0 && (
          <>
            {cat === 'all' && own.length > 0 && (
              <RichTextContent
                path="gallerymosaic.places_title"
                as="h2"
                className="mx-0 mb-0 mt-8 font-heading text-[28px] font-light leading-[34px] tracking-[--q-tracking-tight] [&_p]:m-0 max-[767px]:mt-4 max-[767px]:text-[24px] max-[767px]:leading-[30px]"
                defaultValue={toParagraph(city ? `Around ${city}` : undefined)}
              />
            )}
            <div className={mosaic}>{near.map((t, i) => tile(t, i, own.length, near.length))}</div>
          </>
        )}
      </EditableCollectionBinding>

      <Lightbox items={lbItems} index={lb.index} onClose={lb.close} onIndex={lb.setIndex} cta={{ label: 'View Floor Plans', href: PATHS.floorPlans }} />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryMosaic',
  displayName: 'Gallery Mosaic',
  tokens: {
    'gallerymosaic.places_title': {
      label: 'Neighborhood Photos Heading',
      description: 'Until edited, shows "Around" and the city. Shown above the neighborhood photos in the All view.',
      type: 'richtext',
      defaultValue: '<p>Around the neighborhood</p>',
    },
  },
  collectionBindings: {
    'gallerymosaic.photos': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
    },
    'gallerymosaic.places': {
      label: 'Neighborhood Photos',
      collection: 'neighborhood_pois',
      defaultLimit: 12,
    },
  },
};

(GalleryMosaic as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryMosaic;
